<template>
  <div>
    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p>Loading exam...</p>
    </div>

    <div v-else-if="!examStarted" class="exam-intro">
      <h2>{{ examName }}</h2>
      <div v-if="examDescription" class="exam-description mb-4" v-html="examDescription">
      </div>

      <div v-if="needPassword" class="password-section mb-4">
        <label for="examPassword" class="form-label">Enter exam password:</label>
        <input 
          v-model="enteredPassword" 
          type="password" 
          class="form-control"
          id="examPassword"
          style="max-width: 300px;"
        />
        <button 
          @click="validatePassword" 
          class="btn btn-primary mt-2"
          :disabled="!enteredPassword.trim()"
        >
          Start Exam
        </button>
      </div>

      <div v-else class="start-section">
        <div v-if="hasProgress" class="alert alert-info">
          <h5>Resume Exam</h5>
          <p>You have an exam in progress. You can continue where you left off.</p>
        </div>
        <button @click="startExam" class="btn btn-success mt-3">
          {{ hasProgress ? 'Continue Exam' : 'Start Exam' }}
        </button>
      </div>
    </div>

    <div v-else-if="examCompleted" class="exam-completed">
      <div class="alert" :class="examProgress.passed ? 'alert-success' : 'alert-warning'">
        <h3>Exam Completed!</h3>
        <p>
          Score: {{ examProgress.questionsCorrect }} / {{ examProgress.questionsTotal }}
          ({{ Math.round((examProgress.questionsCorrect / examProgress.questionsTotal) * 100) }}%)
        </p>
        <p class="mt-2">
          <strong>Result: {{ examProgress.passed ? 'PASSED' : 'FAILED' }}</strong>
        </p>
        <div v-if="examProgress.instructorFeedback" class="mt-3">
          <h5>Instructor Feedback:</h5>
          <p>{{ examProgress.instructorFeedback }}</p>
        </div>
      </div>
      
      <!-- Retake button for failed exams if multiple attempts allowed -->
      <div v-if="!examProgress.passed && allowRetries" class="mt-3">
        <button @click="retakeExam" class="btn btn-warning mt-3">
          Retake Exam
        </button>
      </div>
    </div>

    <div v-else class="exam-content">
      <div class="exam-header mb-4">
        <h3>{{ examName }}</h3>
        <div class="progress-info">
          <span>{{ questions.length }} Questions</span>
        </div>
      </div>

      <div class="questions-container">
        <exam-question 
          v-for="(question, index) in questions" 
          :key="question.id"
          :question="question" 
          :question-number="index + 1"
          :initial-answer="answers[question.id]"
          @answer-selected="handleAnswerSelected"
          class="mb-5"
        />
      </div>

      <div class="mt-4">
        <!-- Progress Callout -->
        <div class="progress-callout mb-4 p-3 rounded" :class="allQuestionsAnswered ? 'bg-green-100 border border-green-300' : 'bg-yellow-100 border border-yellow-300'">
          <div class="flex items-center">
            <svg v-if="allQuestionsAnswered" class="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <svg v-else class="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span class="font-medium" :class="allQuestionsAnswered ? 'text-green-800' : 'text-yellow-800'">
              You have answered {{ answeredQuestionsCount }} out of {{ questions.length }} questions
            </span>
          </div>
          <p v-if="!allQuestionsAnswered" class="text-sm text-yellow-700 mt-1 ml-7">
            Please answer all questions before finishing the exam.
          </p>
        </div>

        <div class="button-container text-center">
          <button 
            @click="finishExam" 
            class="btn btn-success"
            :disabled="!allQuestionsAnswered"
          >
            Finish Exam
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import ExamQuestion from './ExamQuestion.vue';

export default {
  name: 'ExamTakingPage',
  components: {
    ExamQuestion
  },
  props: {
    examId: {
      type: Number,
      required: true
    },
    examName: {
      type: String,
      required: true
    },
    examDescription: {
      type: String,
      default: ''
    },
    hasProgress: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const loading = ref(true);
    const examStarted = ref(false);
    const examCompleted = ref(false);
    const needPassword = ref(false);
    const enteredPassword = ref('');
    const questions = ref([]);
    const answers = ref({});
    const examProgress = ref(null);
    const allowRetries = ref(false);

    const allQuestionsAnswered = computed(() => {
      return questions.value.every(q => answers.value[q.id] !== undefined);
    });

    const answeredQuestionsCount = computed(() => {
      return Object.keys(answers.value).length;
    });

    const loadExamQuestions = async () => {
      try {
        const { data } = await axios.get(`/api/exams/${props.examId}/questions`);
        questions.value = data;
      } catch (error) {
        console.error('Error loading exam questions:', error);
        alert('Failed to load exam questions. Please try again.');
      }
    };

    const loadExamProgress = async () => {
      try {
        const { data } = await axios.get(`/api/exams/${props.examId}/progress`);
        if (data) {
          examProgress.value = data;
          
          // Load previously saved answers into local state so user can see their selections
          if (data.questionAnswers && data.questionAnswers.length > 0) {
            data.questionAnswers.forEach(qa => {
              if (qa.selectedAnswerId) {
                // Single choice question
                answers.value[qa.quizQuestionId] = {
                  questionId: qa.quizQuestionId,
                  type: 'choice',
                  value: qa.selectedAnswerId
                };
              } else if (qa.selectedAnswerIds && qa.selectedAnswerIds.length > 0) {
                // Multiple choice question
                answers.value[qa.quizQuestionId] = {
                  questionId: qa.quizQuestionId,
                  type: 'multiple',
                  value: qa.selectedAnswerIds
                };
              } else if (qa.selectedAnswer) {
                // Text answer
                answers.value[qa.quizQuestionId] = {
                  questionId: qa.quizQuestionId,
                  type: 'text',
                  value: qa.selectedAnswer
                };
              } else if (qa.fileUploads && qa.fileUploads.length > 0) {
                // File upload - we can't restore actual files, but we can show that files were uploaded
                answers.value[qa.quizQuestionId] = {
                  questionId: qa.quizQuestionId,
                  type: 'file',
                  value: [] // Can't restore actual file objects, but answer is marked as answered
                };
              }
            });
          }
          
          // If the exam is marked as completed in the progress data, show the completion callout
          // This allows users to see their results when they return to a completed exam
          if (data.completed) {
            examCompleted.value = true;
          }
        }
      } catch (error) {
        console.error('Error loading exam progress:', error);
      }
    };

    const startExam = async () => {
      try {
        if (!props.hasProgress) {
          await axios.post(`/api/exams/${props.examId}/start`);
        }
        examStarted.value = true;
      } catch (error) {
        console.error('Error starting exam:', error);
        alert('Failed to start exam. Please try again.');
      }
    };

    const validatePassword = () => {
      // This would need to be implemented with proper password validation
      // For now, just start the exam
      examStarted.value = true;
      needPassword.value = false;
    };

    const handleAnswerSelected = async (answer) => {
      // Update local state immediately
      answers.value[answer.questionId] = answer;
      
      // Save answer to database immediately
      try {
        await submitAnswer(answer.questionId, answer);
        console.log('Answer saved successfully for question:', answer.questionId);
      } catch (error) {
        console.error('Failed to save answer for question:', answer.questionId, error);
        // Don't remove from local state - let user continue but show warning
        // They can try again by changing their answer or finishing the exam
      }
    };

    const finishExam = async () => {
      try {
        // Since answers are now saved immediately when selected, we only need to
        // ensure any pending answers are saved and reload progress to get final results
        
        // Submit any answers that might not have been saved yet (in case of previous errors)
        const unsavedAnswers = Object.entries(answers.value).filter(([questionId, answer]) => {
          // We could add logic here to track which answers failed to save, but for now
          // we'll just resubmit all answers to be safe
          return true;
        });
        
        for (const [questionId, answer] of unsavedAnswers) {
          try {
            await submitAnswer(questionId, answer);
          } catch (error) {
            console.error('Error resubmitting answer during finish:', error);
            // Continue with other answers - don't fail the entire finish process
          }
        }
        
        // Add a small delay to ensure backend processing is complete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Reload progress to get final results
        await loadExamProgress();
        examCompleted.value = true;
        
        // Emit the exam-completed event to notify parent component
        emit('exam-completed');
      } catch (error) {
        console.error('Error finishing exam:', error);
        alert('Failed to submit exam. Please try again.');
      }
    };

    const submitAnswer = async (questionId, answer) => {
      try {
        const formData = new FormData();
        formData.append('QuizQuestionId', questionId);
        
        if (answer.type === 'text') {
          formData.append('TextAnswer', answer.value);
          // For text questions, any submission (even empty) should be valid
        } else if (answer.type === 'choice') {
          formData.append('QuizQuestionAnswerChoiceId', answer.value);
        } else if (answer.type === 'multiple') {
          formData.append('AnswerChoiceIds', answer.value.join(','));
        } else if (answer.type === 'file') {
          // For file uploads, any submission (even no files) should be valid
          if (answer.value && answer.value.length > 0) {
            answer.value.forEach(file => {
              formData.append('Files', file);
            });
          }
        }

        await axios.post('/api/exams/answers', formData);
      } catch (error) {
        console.error('Error submitting answer:', error);
        throw error;
      }
    };

    const loadExamDetails = async () => {
      try {
        const { data } = await axios.get(`/api/exams/${props.examId}`);
        allowRetries.value = data.allowMultipleAttempts === true || data.allowMultipleAttempts === 'true';
      } catch (error) {
        console.error('Error loading exam details:', error);
      }
    };

    const retakeExam = async () => {
      if (confirm('Are you sure you want to retake this exam? This will reset your current progress.')) {
        try {
          await axios.delete(`/api/exams/${props.examId}/progress`);
          
          // Reset component state
          examCompleted.value = false;
          examStarted.value = false;
          examProgress.value = null;
          answers.value = {};
          
          // Reload questions and start fresh
          await loadExamQuestions();
          examStarted.value = true;
        } catch (error) {
          console.error('Error resetting exam:', error);
          alert('Failed to reset exam. Please try again.');
        }
      }
    };

    onMounted(async () => {
      try {
        await loadExamDetails();
        await loadExamQuestions();
        await loadExamProgress();
        
        if (props.hasProgress && !examCompleted.value) {
          examStarted.value = true;
        }
      } catch (error) {
        console.error('Error initializing exam:', error);
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      examStarted,
      examCompleted,
      needPassword,
      enteredPassword,
      questions,
      answers,
      examProgress,
      allQuestionsAnswered,
      answeredQuestionsCount,
      allowRetries,
      startExam,
      validatePassword,
      handleAnswerSelected,
      finishExam,
      retakeExam
    };
  }
};
</script>

<style scoped>
.questions-container {
  margin-bottom: 30px;
}

.progress-info {
  color: #666;
  font-size: 14px;
}

.exam-navigation {
  display: flex;
  justify-content: center;
  align-items: center;
}

.gap-2 {
  gap: 0.5rem;
}
</style>